import React from "react";
import styled from "styled-components";
import { colors } from "../../../../theme";

//styles
const Timeline = styled.div`
  position: relative;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 3rem;
  padding-right: 0;
  padding-top: 2rem;
  scroll-margin-top: 100px;

  &:first-child {
    padding-top: 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0.75rem;
    height: 100%;
    border-right: 1px dashed ${colors.linkHover.dark};
  }

  &::after {
    content: "";
    position: absolute;
    top: 2.5rem;
    left: 0.5rem;
    width: 0.5rem;
    height: 0.5rem;
    background: ${colors.linkHover.dark};
    border-radius: 9999px;
    outline: 8px solid ${colors.surface};
  }

  &:first-child::after {
    top: 0.5rem;
  }

  @media (min-width: 768px) {
    padding-left: 3.5rem;
    padding-right: 1rem;

    &::before {
      left: 1.25rem;
    }

    &::after {
      left: 1rem;
    }
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
`;

const Role = styled.h4`
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.35;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const Period = styled.div`
  flex-shrink: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #374151;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const Company = styled.h5`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
  color: #374151;

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.25rem;
  }
`;

const Description = styled.div`
  margin-top: 1.25rem;

  ul {
    list-style: disc;
    padding-left: 1rem;
  }

  li {
    padding-left: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #374151;

    &::marker {
      color: #9ca3af;
      font-size: 0.75rem;
    }

    @media (min-width: 768px) {
      font-size: 0.875rem;
      line-height: 1.5rem;
    }
  }

  @media (min-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const TechStack = styled.div`
  margin-top: 1rem;
  font-size: 0.6875rem;
  line-height: 1.4;
  color: #374151;

  @media (min-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.5rem;
  }
`;

const WORK_EXPERIENCE = [
  {
    id: "work-meltwater",
    role: "Senior front-end developer",
    company: "Meltwater Group",
    period: "2022 - Present",
    bullets: [
      "Led front-end development for a publishing web app using Vue, SCSS, Apollo GraphQL, Web workers, WebSockets, and i18n.",
      "Built over 70 technology-agnostic components for an internal design library using Stencil.js (web components).",
      "Led cross-team frontend initiatives consistently to improve CI/CD flow and enhance code quality.",
      "Worked on a serverless AWS back end consisting of Lambda, S3, CloudWatch, SNS, SQS, MongoDB Atlas, and Terraform.",
      "Conducted performance audits, improving time to interactive (TTI) by 37% and reducing memory usage by 30% using IndexedDB and service workers.",
      "Migrated CI/CD pipelines from Drone to GitHub Actions and set up preview branches.",
      "Led a pod with 5-6 senior software engineers and a QA team.",
      "Operated in a fully remote, EST time zone, using Scrum and Kanban workflows.",
      "Participated in rotational on-call duties on a weekly basis, every month.",
    ],
    technologies:
      "Stencil.js, TypeScript, Vue, HTML, SCSS, HTML5, Figma, MongoDB, Amazon Web Services (AWS), Storybook, ESLint, FullCalendar, CSS3 animation, Front-end architecture, i18n, Single-page applications (SPAs), Vite, Swagger, Data transformation, Gulp, API integration, Responsive layout, ECMAScript (ES6), Responsive web design (RWD), React Query, Component libraries, Design systems, Cypress, Figma to React, REST, MobX, Playwright, UI testing, Software architecture, CI/CD pipelines, Analytics, Reusable libraries, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Back-end development, Dashboards, Accessibility, Monorepos, WCAG 2, React Native, Web content accessibility guidelines (WCAG), Docker, Data visualization, Svelte, Turborepo",
  },
  {
    id: "work-speakeasy",
    role: "Senior front-end focused full-stack developer",
    company: "Speakeasy Events, Inc.",
    period: "2022 - 2022",
    bullets: [
      "Developed the front-end for an MVP website (Redwood.js, React) of a US-based edtech firm that encourages children aged between 10 and 13 years to write and read for fun.",
      "Developed GraphQL-based APIs using RedwoodJS, Node.js, and AWS Lambda.",
      "Tracked and followed features, changes, and bugs using Pivotal Tracker as a reporting tool.",
      "Delivered the project a couple of weeks before the client's deadline.",
      "Worked in an Agile-driven environment.",
      "Implemented interactive components such as a rich text format (RTF) text editor and drag-and-drop file upload.",
    ],
    technologies:
      "React, RedwoodJS, GraphQL, TypeScript, Agile, daisyUI, Tailwind CSS, JSX, Stripe, Mailchimp, Node.js, JavaScript, HTML5, CSS, Figma, Pivotal Tracker, API development, Full-stack, Web development, UI development, AWS Lambda, Visual Studio Code (VS Code), Git, MacOS, Express.js, SCSS, Software development lifecycle (SDLC), English, Database management systems (DBMS), Data structures, HTML, GitHub, Front-end, CSS3, Full-stack development, Startups, APIs, JSON, Webpack, Responsive UI, Vercel, Animation, Amazon Web Services (AWS), CSS3 animation, Front-end architecture, Prisma, i18n, Single-page applications (SPAs), Data transformation, API integration, Responsive layout, ECMAScript (ES6), Responsive web design (RWD), React Query, Cypress, Figma to React, REST, MobX, Playwright, UI testing, Software architecture, Analytics, Reusable libraries, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Back-end development, Dashboards, Accessibility, WCAG 2, Web content accessibility guidelines (WCAG), Serverless framework",
  },
  {
    id: "work-ziyang",
    role: "Senior front-end developer",
    company: "Ziyang Gong",
    period: "2022 - 2022",
    bullets: [
      "Developed a pixel-perfect front-end of a Web3 NFT marketplace web app from scratch using Next.js (React), JavaScript, Chakra UI, and Storybook. It enabled the users to list and buy NFTs, respectively. Also, it had exclusive merchandise listed on it.",
      "Revamped the look and feel of an existing website built using Next.js (React), TypeScript, and styled components. Also, UIs were developed for five to six new dynamic pages, and APIs, Spotify player SDK, and crypto wallets were integrated into them.",
      "Collaborated and actively integrated with the team spread across different time zones to ensure the product quality was on par with the client's expectations.",
    ],
    technologies:
      "React, TypeScript, JavaScript, Chakra UI, Context API, Material UI, Styled-components, Docker, APIs, Next.js, CSS, HTML, JSX, HTML5, Adobe experience design (XD), Netlify, GoDaddy, Deployment, Storybook, Canvas, Spotify API, Framer, Web development, Front-end, Front-end development, GSAP, GreenSock animation platform (GSAP), Visual Studio Code (VS Code), Git, MacOS, SCSS, Software development lifecycle (SDLC), English, Data structures, GitHub, CSS3, Startups, Web3.js, JSON, Webpack, Responsive UI, Vercel, Animation, Amazon Web Services (AWS), CSS3 animation, Front-end architecture, Web3, i18n, Single-page applications (SPAs), Data transformation, API integration, Responsive layout, ECMAScript (ES6), Responsive web design (RWD), Figma to React, REST, MobX, Playwright, UI testing, Software architecture, Reusable libraries, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Dashboards, Shadcn, Accessibility, WCAG 2, Web content accessibility guidelines (WCAG)",
  },
  {
    id: "work-freelance-clients",
    role: "Freelance developer",
    company: "Freelance clients",
    period: "2021 - 2022",
    bullets: [
      "Helped an Indian manufacturing firm land over ten new international clients and increased their sales by 25% in the first quarter of the launch by establishing their online identity and developing their product catalog website.",
      "Boosted performance by reducing initial load time down to 0.3 seconds.",
      "Achieved 100% compliance with industry best practices.",
      "Optimized designs to increase the retention rate among the target audience.",
      "Built a scalable React front end for a 100,000+ user loyalty program with SWR-based optimistic updates.",
    ],
    technologies:
      "JavaScript, HTML5, CSS, React, Redux, Redux form, Next.js, Gatsby, Contentful, Material UI, TypeScript, GraphQL, Firebase, HTML, Stripe API, Figma, GitHub, Front-end, Tailwind CSS, RedwoodJS, Chakra UI, Cloudinary, Google SEO, SEO tools, CSS3, NPM, Sass, Zustand, Front-end development, Web components, APIs, Storybook, JSON, Atomic design, User interface (UI), Webpack, Headless CMS, Responsive UI, MUI CSS, React Redux, Google Cloud Platform (GCP), JSON Web Tokens (JWT), API development, eCommerce, React Router, TypeScript 3, REST APIs, JSX, Context API, Mailchimp, Firebase hosting, Visual Studio Code (VS Code), Git, MacOS, Express.js, Agile, UI design, SCSS, Software development lifecycle (SDLC), Web development, English, Database management systems (DBMS), Data structures, Bootstrap, Startups, Data visualization, Styled-components, UI components, Content management systems (CMS), Chart.js, Animation, Visualization design, User experience (UX), Amazon Web Services (AWS), Strapi, CSS3 animation, Front-end architecture, Single-page applications (SPAs), Data transformation, Middleware, RESTFul APIs, Gulp, API integration, Responsive layout, ECMAScript (ES6), Lottie, Responsive web design (RWD), Figma to React, REST, MobX, UI testing, Reusable libraries, Application state management, Pixel perfect, Mobile first, UX design, Mobile-first design, Responsiveness, Back-end development, Dashboards, Accessibility, WCAG 2, Web content accessibility guidelines (WCAG)",
  },
  {
    id: "work-classic-informatics",
    role: "Software engineer",
    company: "Classic Informatics",
    period: "2019 - 2021",
    bullets: [
      "Led front-end development of a payment gateway web application using React, Redux, Redux-saga, Material UI, and Stripe to simplify online payments for Italian citizens.",
      "Acted as a front-end developer. Developed a property management dashboard application using React, Redux, Redux-Saga, React-Bootstrap, and Stripe to simplify handling properties for landlords.",
      "Helped interview more than 50 candidates for React and React Native developer positions, evaluated them technically and for culture fit, and provided feedback.",
      "Trained over 25 design team members on JavaScript and React.",
      "Collaborated and actively interacted with clients from Europe.",
      "Collaborated closely with the UI/UX team to create pixel-perfect visuals.",
      "Worked in an Agile-driven environment.",
    ],
    technologies:
      "React, Redux, Redux-Saga, Material UI, Bootstrap, JavaScript, HTML5, CSS, Agile, Stripe, HTML, Stripe API, Stripe payments, GitHub, Front-end, Chakra UI, Google SEO, SEO tools, CSS3, NPM, Sass, Zustand, Redux form, Next.js, Gatsby, Front-end development, Web components, APIs, Storybook, JSON, Atomic design, Webpack, Responsive UI, MUI CSS, React Redux, JSON Web Tokens (JWT), API development, React Router, REST APIs, JSX, Context API, Mailchimp, Visual Studio Code (VS Code), Git, MacOS, SCSS, Software development lifecycle (SDLC), Web development, English, Data structures, Redux Thunk, Apollo, React Apollo, Startups, Animation, CSS3 animation, Front-end architecture, Single-page applications (SPAs), Data transformation, API integration, Responsive layout, ECMAScript (ES6), Responsive web design (RWD), Figma to React, REST, MobX, UI testing, Reusable libraries, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Google Maps, Dashboards, Accessibility, WCAG 2, Web content accessibility guidelines (WCAG)",
  },
  {
    id: "work-credit-culture",
    role: "Full-stack developer",
    company: "Credit Culture",
    period: "2019 - 2019",
    bullets: [
      "Guided a team of four employees in developing a portal for an easy loan application.",
      "Developed an AWS-powered back end using Lambda functions, DynamoDB, and an API Gateway.",
      "Integrated back-end APIs into React-based front end using JavaScript, React, Redux, Redux Thunk, and Sass.",
    ],
    technologies:
      "React, JavaScript, Redux Thunk, CSS, HTML5, Bootstrap, SCSS, HTML, Node.js, GitHub, Front-end, Google SEO, SEO tools, CSS3, NPM, Sass, Redux form, Front-end development, Web components, APIs, JSON, Webpack, Responsive UI, MUI CSS, React Redux, JSON Web Tokens (JWT), API development, React Router, REST APIs, JSX, Banking & finance, Full-stack, AWS Lambda, Git, Visual Studio Code (VS Code), MacOS, Express.js, Agile, Software development lifecycle (SDLC), Web development, English, Database management systems (DBMS), Data structures, Redux, Full-stack development, Apollo, React Apollo, Startups, Animation, Amazon Web Services (AWS), Fintech, CSS3 animation, Front-end architecture, Single-page applications (SPAs), Middleware, Postman, RESTFul APIs, API integration, Responsive layout, ECMAScript (ES6), Responsive web design (RWD), Figma to React, REST, MobX, UI testing, Reusable libraries, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Gaming, Dashboards, Accessibility, WCAG 2, Web content accessibility guidelines (WCAG)",
  },
  {
    id: "work-freelance",
    role: "React developer",
    company: "Freelance",
    period: "2019 - 2019",
    bullets: [
      "Built a marketplace website for farmers and associated businesses using React, Redux, Redux Thunk, SasS, npm, Git, HTML, and SASS.",
      "Led front-end development of the website's MVP version, resulting in a fast and stable website.",
      "Improved customer satisfaction by 12% by suggesting UX changes in the onboarding flow.",
      "Delivered a project a week before the scheduled deadline.",
    ],
    technologies:
      "React, Redux, Redux-Saga, HTML5, JavaScript, CSS, HTML, Node.js, GitHub, Front-end, Google SEO, SEO tools, CSS3, NPM, Sass, Startups, Front-end development, Web components, APIs, JSON, Atomic design, User interface (UI), Webpack, Responsive UI, React Redux, JSON Web Tokens (JWT), API development, eCommerce, React Router, REST APIs, JSX, Git, Visual Studio Code (VS Code), MacOS, Agile, UI design, User experience (UX), SCSS, Software development lifecycle (SDLC), Web development, English, Database management systems (DBMS), Data structures, Redux Thunk, Bootstrap, Figma, UI components, Animation, CSS3 animation, Front-end architecture, Single-page applications (SPAs), Postman, RESTFul APIs, Gulp, API integration, ECMAScript (ES6), Responsive web design (RWD), Figma to React, REST, MobX, UI testing, Application state management, Pixel perfect, Mobile first, Mobile-first design, Responsiveness, Dashboards, Accessibility, Web content accessibility guidelines (WCAG)",
  },
];

function WorkTimelineItem({
  id,
  role,
  company,
  period,
  bullets,
  technologies,
}) {
  return (
    <TimelineItem id={id}>
      <TimelineHeader>
        <Role>{role}</Role>
        <Period>{period}</Period>
      </TimelineHeader>

      <Company>{company}</Company>

      <Description>
        <ul>
          {bullets.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </Description>

      {technologies && (
        <TechStack>
          <strong>Technologies:</strong> {technologies}
        </TechStack>
      )}
    </TimelineItem>
  );
}

export default function WorkTimeline() {
  return (
    <Timeline>
      {WORK_EXPERIENCE.map((job) => (
        <WorkTimelineItem
          key={job.id}
          id={job.id}
          role={job.role}
          company={job.company}
          period={job.period}
          bullets={job.bullets}
          technologies={job.technologies}
        />
      ))}
    </Timeline>
  );
}
